import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { MessageCircle } from 'lucide-react';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import SentMessagesFilters from './components/SentMessagesFilters';
import SentMessageDialog from './components/SentMessageDialog';
import SentMessagesCounter from './components/SentMessagesCounter';
import {
  TRACKING_START_DATE,
  messageTypeLabels,
  useSentMessagesScreen,
} from './hooks/useSentMessagesScreen';

export default function SentMessagesMobile() {
  const {
    isLoading,
    error,
    selectedMonth,
    setSelectedMonth,
    messages,
    isBeforeTrackingStart,
    selectedMessage,
    openMessage,
    closeMessage,
    openReference,
  } = useSentMessagesScreen();

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    setShowErrorMessage(Boolean(error));
  }, [error]);

  return (
    <Box sx={{ pb: 2 }}>
      <Loading isToHidden={isLoading === false} />

      {showErrorMessage && (
        <ErrorMessage message={error} show={showErrorMessage} setShow={setShowErrorMessage} />
      )}

      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>
          <MessageCircle size={20} />
        </Box>
        <Typography variant="subtitle1">Mensagens Enviadas</Typography>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
          <SentMessagesFilters
            isMobile
            selectedMonth={selectedMonth}
            onChangeMonth={setSelectedMonth}
          />
          <SentMessagesCounter total={messages.length} selectedMonth={selectedMonth} />
        </Stack>

        {isBeforeTrackingStart && (
          <Alert severity="warning" sx={{ mb: 2 }} data-cy="sent-messages-tracking-notice">
            Essa funcionalidade só começou a contar as mensagens a partir de{' '}
            {TRACKING_START_DATE.format('DD/MM/YYYY')}.
          </Alert>
        )}

        {messages.length === 0 && !isLoading ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            Nenhuma mensagem enviada no mês selecionado.
          </Typography>
        ) : (
          <Card variant="outlined">
            <List disablePadding>
              {messages.map((message) => (
                <ListItemButton
                  key={message.id}
                  divider
                  data-cy="sent-message-row"
                  onClick={() => openMessage(message)}
                >
                  <ListItemText
                    primary={
                      <Stack
                        component="span"
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          noWrap
                        >
                          {message.recipientName || message.recipientPhone || '—'}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                        >
                          {message.sentAt}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5} component="span" sx={{ mt: 0.5 }}>
                        <Chip
                          component="span"
                          label={messageTypeLabels[message.type] ?? message.type}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ alignSelf: 'flex-start' }}
                        />
                        <Typography component="span" variant="caption" noWrap>
                          {message.content}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Card>
        )}
      </Box>

      <SentMessageDialog
        fullScreen
        message={selectedMessage}
        onClose={closeMessage}
        onOpenReference={openReference}
      />
    </Box>
  );
}
